import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api';
import KpiCard from '../components/KpiCard';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU');
}

export default function PromoCodes() {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => {
    api.promo.list().then(setCodes).catch(e => setMsg('Hiba: ' + e.message));
    api.promo.stats().then(setStats).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const filtered = codes.filter(c => {
    if (search && !c.code.toLowerCase().includes(search.toLowerCase()) && !(c.campaign || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterActive === 'active' && !c.isActive) return false;
    if (filterActive === 'inactive' && c.isActive) return false;
    return true;
  });

  const campaignData = stats ? Object.entries(stats.byCampaign).map(([k, v]) => ({ name: k, redemptions: v.redemptions, codes: v.codes })) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Promo kódok</h1>
        <button className="btn" onClick={() => setShowCreate(true)}>+ Új kód</button>
      </div>

      {msg && <div style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
          <KpiCard label="Össz. kód" value={stats.totalCodes} />
          <KpiCard label="Aktív kód" value={stats.totalActive} color="#34d399" />
          <KpiCard label="Összes beváltás" value={stats.totalRedemptions} color="#fbbf24" />
        </div>
      )}

      {stats && campaignData.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="card">
            <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Kampányok beváltás szerint</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={campaignData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
                <XAxis dataKey="name" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
                <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
                <Bar dataKey="redemptions" fill="#00d4ff" radius={[6, 6, 0, 0]} name="Beváltás" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card">
            <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Top 10 kód</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2e3a' }}>
                  <th style={th}>Kód</th><th style={th}>Kampány</th><th style={th}>Beváltva</th>
                </tr>
              </thead>
              <tbody>
                {stats.topCodes.map(c => (
                  <tr key={c.code} style={{ borderBottom: '1px solid #1a1d27' }}>
                    <td style={{ ...td, fontFamily: 'monospace', color: '#00d4ff' }}>{c.code}</td>
                    <td style={td}>{c.campaign || '—'}</td>
                    <td style={td}>{c.usedCount} / {c.maxUses}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <input placeholder="Keresés kód/kampány..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
          <select value={filterActive} onChange={e => setFilterActive(e.target.value)}>
            <option value="all">Mind</option>
            <option value="active">Csak aktív</option>
            <option value="inactive">Csak inaktív</option>
          </select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2e3a' }}>
                <th style={th}>Kód</th>
                <th style={th}>Kampány</th>
                <th style={th}>Influencer</th>
                <th style={th}>POI</th>
                <th style={th}>Felhasználva</th>
                <th style={th}>Lejár</th>
                <th style={th}>Állapot</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #1a1d27' }}>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#00d4ff', fontWeight: 600 }}>{c.code}</td>
                  <td style={td}>{c.campaign || '—'}</td>
                  <td style={td}>{c.influencer || '—'}</td>
                  <td style={td}>{c.poiAmount}</td>
                  <td style={td}>
                    <span style={{ color: c.usedCount >= c.maxUses ? '#f87171' : '#e4e6eb' }}>
                      {c.usedCount || 0} / {c.maxUses}
                    </span>
                  </td>
                  <td style={td}>{fmtDate(c.expiresAt)}</td>
                  <td style={td}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, background: c.isActive ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)', color: c.isActive ? '#34d399' : '#f87171' }}>
                      {c.isActive ? 'AKTÍV' : 'INAKTÍV'}
                    </span>
                  </td>
                  <td style={td}>
                    <button onClick={() => setEditCode(c)} style={smallBtn}>Szerk.</button>
                    <button onClick={async () => {
                      if (!window.confirm(`Biztos törlöd: ${c.code}?`)) return;
                      await api.promo.delete(c.code);
                      flash('Törölve: ' + c.code);
                      load();
                    }} style={{ ...smallBtn, color: '#f87171' }}>Törlés</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#8b8fa3', padding: 24 }}>Nincs találat</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); flash('Kód létrehozva'); load(); }} />}
      {editCode && <EditModal code={editCode} onClose={() => setEditCode(null)} onSaved={() => { setEditCode(null); flash('Mentve'); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ code: '', poiAmount: 25, maxUses: 100, expiresAt: '', campaign: '', influencer: '' });
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    try {
      await api.promo.create(form);
      onSaved();
    } catch (e) { setErr(e.message); }
  };

  return (
    <Modal onClose={onClose} title="Új promo kód">
      {err && <div style={{ color: '#f87171', marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <Field label="Kód (pl. BECKY25)"><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} style={{ fontFamily: 'monospace', width: '100%' }} /></Field>
      <Field label="POI mennyiség"><input type="number" value={form.poiAmount} onChange={e => setForm({ ...form, poiAmount: e.target.value })} style={{ width: '100%' }} /></Field>
      <Field label="Max felhasználás"><input type="number" value={form.maxUses} onChange={e => setForm({ ...form, maxUses: e.target.value })} style={{ width: '100%' }} /></Field>
      <Field label="Lejárat (opcionális)"><input type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} style={{ width: '100%' }} /></Field>
      <Field label="Kampány"><input value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} placeholder="pl. influencer, szolnok_pilot" style={{ width: '100%' }} /></Field>
      <Field label="Influencer (opcionális)"><input value={form.influencer} onChange={e => setForm({ ...form, influencer: e.target.value })} style={{ width: '100%' }} /></Field>
      <button className="btn" onClick={submit} style={{ width: '100%', marginTop: 12 }} disabled={!form.code || !form.poiAmount || !form.maxUses}>Létrehozás</button>
    </Modal>
  );
}

function EditModal({ code, onClose, onSaved }) {
  const [isActive, setIsActive] = useState(code.isActive);
  const [expiresAt, setExpiresAt] = useState(code.expiresAt?.slice(0, 10) || '');
  const [maxUses, setMaxUses] = useState(code.maxUses);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    try {
      await api.promo.update(code.code, { isActive, expiresAt: expiresAt || null, maxUses: Number(maxUses) });
      onSaved();
    } catch (e) { setErr(e.message); }
  };

  return (
    <Modal onClose={onClose} title={`Szerkesztés: ${code.code}`}>
      {err && <div style={{ color: '#f87171', marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <Field label="Állapot">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          {isActive ? 'Aktív' : 'Inaktív'}
        </label>
      </Field>
      <Field label="Lejárat"><input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} style={{ width: '100%' }} /></Field>
      <Field label={`Max felhasználás (jelenlegi: ${code.usedCount || 0})`}><input type="number" value={maxUses} onChange={e => setMaxUses(e.target.value)} style={{ width: '100%' }} /></Field>
      <button className="btn" onClick={submit} style={{ width: '100%', marginTop: 12 }}>Mentés</button>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b8fa3', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#8b8fa3', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 12px', color: '#8b8fa3', fontWeight: 600 };
const td = { padding: '10px 12px', color: '#e4e6eb' };
const smallBtn = { background: 'none', border: '1px solid #2a2e3a', color: '#8b8fa3', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, marginRight: 4 };
